<template>
  <el-scrollbar class="scroll-bar">
    <div
      class="min-h-screen bg-[#0f1729] text-white p-6 font-sans"
      style="background-color: #0f1729"
      v-loading="loadingEvent"
    >
      <div class="max-w-2xl mx-auto">
        <div class="flex items-start justify-between mb-4">
          <div>
            <h1 class="text-3xl font-bold mb-3">Alert Details</h1>
            <p class="text-slate-400 text-base leading-relaxed">
              Complete history and notification log for this alert
            </p>
          </div>
          <button
            class="text-slate-400 hover:text-white transition-colors"
            aria-label="Close"
            type="button"
            @click="handleCloseWindow"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-x"
            >
              <path d="M18 6 6 18"></path>
              <path d="m6 6 12 12"></path>
            </svg>
          </button>
        </div>

        <div
          v-if="errorMessage"
          class="mt-6 rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200"
        >
          {{ errorMessage }}
        </div>

        <div v-else>
          <div v-if="eventDetail" class="mt-12 mb-8">
            <h2 class="text-3xl font-bold mb-6">{{ eventTitle }}</h2>
            <p
              v-if="eventDetail.event"
              class="text-slate-300 leading-relaxed mb-6"
            >
              {{ eventDetail.event }}
            </p>
            <div class="space-y-5">
              <div class="flex items-center justify-between">
                <span class="text-slate-400 text-lg">Device:</span>
                <span class="text-white text-lg font-medium">
                  {{ eventDeviceLabel }}
                </span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-slate-400 text-lg">Level:</span>
                <span class="flex-shrink-0">
                  <RenderEventLogLevel :model-value="eventLevelValue" size="xlarge"/>
                </span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-slate-400 text-lg">Status:</span>
                <span class="alert-status">{{ alertStatusLabel }}</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-slate-400 text-lg">Triggered:</span>
                <span class="text-white text-lg font-medium">
                  {{ triggeredAtLabel }}
                </span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-slate-400 text-lg">Escalation Level:</span>
                <span class="text-white text-lg font-medium">
                  {{ escalationLabel }}
                </span>
              </div>
            </div>
          </div>

          <div
            v-else
            class="mt-12 rounded-lg border border-slate-700/60 bg-slate-800/30 p-6 text-center text-slate-400"
          >
            No alert information available for this link.
          </div>

          <div class="border-t border-slate-700/50 my-8"></div>

          <div class="mt-12">
            <h2 class="text-3xl font-bold mb-8">Provide Feedback</h2>
            <div class="mb-8">
              <h3 class="text-xl font-semibold mb-5">Feedback Type</h3>
              <div class="grid grid-cols-2 gap-4 mb-6">
                <button
                  type="button"
                  class="flex items-center justify-center gap-3 py-4 px-6 rounded-lg border-2 transition-all bg-slate-800/30"
                  :class="
                    feedbackType === 'True'
                      ? 'border-green-500 text-green-500 hover:border-green-400'
                      : 'border-slate-700 text-slate-300 hover:border-slate-600'
                  "
                  @click="selectFeedback('True')"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-thumbs-up"
                  >
                    <path d="M7 10v12"></path>
                    <path
                      d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"
                    ></path>
                  </svg>
                  <span class="text-lg font-medium">True</span>
                </button>
                <button
                  type="button"
                  class="flex items-center justify-center gap-3 py-4 px-6 rounded-lg border-2 transition-all bg-slate-800/30"
                  :class="
                    feedbackType === 'False'
                      ? 'border-red-500 text-red-500'
                      : 'border-slate-700 text-slate-300 hover:border-slate-600'
                  "
                  @click="selectFeedback('False')"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-thumbs-down"
                    :class="[
                      feedbackType === 'False'
                        ? 'fill-red-500'
                        : 'fill-green-500',
                    ]"
                  >
                    <path d="M17 14V2"></path>
                    <path
                      d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22h0a3.13 3.13 0 0 1-3-3.88Z"
                    ></path>
                  </svg>
                  <span class="text-lg font-medium">False</span>
                </button>
              </div>
              <el-input
                v-model="feedbackText"
                type="textarea"
                :rows="4"
                placeholder="Add more context or actions taken"
                maxlength="500"
                show-word-limit
                class="mb-4"
              />
              <div v-if="feedbackType === 'False'" class="pause-alert-panel">
                <div class="pause-alert-title">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  <span>Pause Alert Until</span>
                </div>
                <div class="pause-alert-grid">
                  <div class="pause-alert-field">
                    <label class="pause-alert-label">Date</label>
                    <el-date-picker
                      v-model="pauseAlertDate"
                      type="date"
                      placeholder="Select date"
                      value-format="yyyy-MM-dd"
                      format="yyyy/MM/dd"
                      style="width: 100%"
                    />
                  </div>
                  <div class="pause-alert-field">
                    <label class="pause-alert-label">Time</label>
                    <el-time-picker
                      v-model="pauseAlertTime"
                      placeholder="Select time"
                      value-format="HH:mm:ss"
                      format="HH:mm"
                      style="width: 100%"
                    />
                  </div>
                </div>
                <p class="pause-alert-hint">
                  The alert will be paused and automatically resume at the specified time.
                </p>
              </div>
            </div>
            <div class="grid grid-cols-3 gap-4">
              <el-button
                data-slot="button"
                class="col-span-1 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border shadow-xs dark:bg-input/30 dark:border-input dark:hover:bg-input/50 h-9 px-4 has-[>svg]:px-3 py-6 text-lg font-medium bg-slate-800/30 border-slate-700 text-slate-300 hover:bg-slate-800/50 hover:text-white"
                @click="handleCloseWindow"
              >
                Close
              </el-button>
              <div class="col-span-2 flex items-center">
                <el-button
                  class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md transition-all disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive h-9 px-4 has-[>svg]:px-3 py-6 text-lg font-medium bg-amber-600 hover:bg-amber-700 text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed w-full"
                  size="large"
                  type="primary"
                  :loading="submittingFeedback"
                  :disabled="!canSubmit"
                  @click="handleSubmitFeedback"
                >
                  Submit Feedback
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </el-scrollbar>
</template>

<script>
import {
  getPublicNotificationEvent,
  updatePublicNotificationEventStatus,
  createPublicNotificationAiAudit,
  updatePublicNotificationAiAudit,
} from "@/api";
import { EventLogStatus, enumValueToLabel } from "@/enums";
import RenderEventLogLevel from "@/components/RenderEventLogLevel.vue";

export default {
  name: "AlertDetailFromWhatsApp",
  components: {
    RenderEventLogLevel,
  },
  data() {
    return {
      token: (this.$route.query.token || "").trim(),
      loadingEvent: false,
      submittingFeedback: false,
      updatingStatus: false,
      eventDetail: null,
      aiAuditList: [],
      feedbackType: "",
      feedbackText: "",
      pauseAlertDate: "",
      pauseAlertTime: "",
      activeAuditId: null,
      errorMessage: "",
    };
  },
  computed: {
    eventTitle() {
      if (!this.eventDetail) {
        return "Alert";
      }
      return this.eventDetail.name || this.eventDetail.event || "Alert";
    },
    eventLevelValue() {
      if (!this.eventDetail || typeof this.eventDetail.level === "undefined") {
        return undefined;
      }
      return this.eventDetail.level;
    },
    alertStatusLabel() {
      if (!this.eventDetail || this.eventDetail.status === undefined) {
        return "--";
      }
      const status = this.eventDetail.status;
      const label = enumValueToLabel(EventLogStatus, status);
      if (label !== "--") {
        return label;
      }
      if (typeof status === "string" && status.length) {
        return status.charAt(0).toUpperCase() + status.slice(1);
      }
      return status;
    },
    triggeredAtLabel() {
      if (!this.eventDetail) {
        return "--";
      }
      const timestamp =
        this.eventDetail.isoTimeString ||
        this.eventDetail.timestamp ||
        this.eventDetail.createdAt ||
        this.eventDetail.sentAt;
      return this.formatTimestamp(timestamp);
    },
    escalationLabel() {
      if (!this.eventDetail || this.eventDetail.escalated === undefined) {
        return "--";
      }
      if (!this.eventDetail.escalated) {
        return "None";
      }
      return `Level ${this.eventDetail.escalated}`;
    },
    eventDeviceLabel() {
      if (!this.eventDetail) {
        return "--";
      }
      return this.eventDetail.assetName || this.eventDetail.deviceKey || "--";
    },
    canSubmit() {
      return (
        !!this.token &&
        !!this.feedbackType &&
        (this.feedbackType === "True" || !!this.feedbackText) &&
        !this.submittingFeedback
      );
    },
  },
  watch: {
    "$route.query.token": {
      immediate: false,
      handler(nextToken) {
        this.token = (nextToken || "").trim();
        this.initialize();
      },
    },
  },
  created() {
    this.initialize();
  },
  methods: {
    async initialize() {
      this.eventDetail = null;
      this.aiAuditList = [];
      this.errorMessage = "";
      if (!this.token) {
        this.errorMessage =
          "Missing token. Please open the link from the notification message.";
        return;
      }
      await this.fetchEventDetail();
    },
    async fetchEventDetail() {
      this.loadingEvent = true;
      try {
        const data = await getPublicNotificationEvent(this.token);
        this.eventDetail = data && data.event ? data.event : null;
        const historyList = data && data.aiAuditList ? data.aiAuditList : [];
        const eventAudit =
          this.eventDetail && this.eventDetail.aiAudit
            ? this.eventDetail.aiAudit
            : null;

        if (eventAudit) {
          this.activeAuditId = eventAudit.id;
          this.feedbackType = eventAudit.type || "";
          this.feedbackText = eventAudit.feedback || "";
          this.pauseAlertDate = eventAudit.pauseAlertDate || "";
          this.pauseAlertTime = eventAudit.pauseAlertTime || "";
        } else if (historyList.length) {
          const latest = historyList[0];
          this.activeAuditId = latest.id;
          this.feedbackType = latest.type || "";
          this.feedbackText = latest.feedback || "";
          this.pauseAlertDate = latest.pauseAlertDate || "";
          this.pauseAlertTime = latest.pauseAlertTime || "";
        } else {
          this.activeAuditId = null;
          this.feedbackType = "";
          this.feedbackText = "";
          this.pauseAlertDate = "";
          this.pauseAlertTime = "";
        }

        if (
          eventAudit &&
          (!historyList.length || historyList[0].id !== eventAudit.id)
        ) {
          this.aiAuditList = [eventAudit, ...historyList];
        } else if (historyList.length) {
          this.aiAuditList = historyList;
        } else if (eventAudit) {
          this.aiAuditList = [eventAudit];
        } else {
          this.aiAuditList = [];
        }
      } catch (error) {
        const message =
          (error && error.message) ||
          (error && error.msg) ||
          "Failed to load alert details.";
        this.errorMessage = message;
      } finally {
        this.loadingEvent = false;
      }
    },
    selectFeedback(type) {
      this.feedbackType = type;
      if (type !== "False") {
        this.pauseAlertDate = "";
        this.pauseAlertTime = "";
      }
    },
    formatTimestamp(value) {
      if (!value) {
        return "--";
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return String(value);
      }
      return date.toLocaleString();
    },
    async handleCloseAlert() {
      if (!this.token || !this.eventDetail) {
        return;
      }
      this.updatingStatus = true;
      try {
        await updatePublicNotificationEventStatus(this.token, { status: 1 });
        this.notify("success", "Alert marked as closed.");
        await this.fetchEventDetail();
      } catch (error) {
        const message =
          (error && error.message) ||
          (error && error.msg) ||
          "Failed to update alert status.";
        this.notify("error", message);
      } finally {
        this.updatingStatus = false;
      }
    },
    async handleSubmitFeedback() {
      if (!this.canSubmit) {
        this.notify("warning", "Select a feedback type and enter details.");
        return;
      }
      this.submittingFeedback = true;
      const payload = {
        type: this.feedbackType,
        feedback: this.feedbackText.trim(),
      };
      try {
        if (this.activeAuditId) {
          await updatePublicNotificationAiAudit(this.token, {
            id: this.activeAuditId,
            ...payload,
          });
        } else {
          await createPublicNotificationAiAudit(this.token, payload);
        }
        this.notify("success", "Feedback submitted successfully.");
        await this.fetchEventDetail();
      } catch (error) {
        const message =
          (error && error.message) ||
          (error && error.msg) ||
          "Failed to submit feedback.";
        this.notify("error", message);
      } finally {
        this.submittingFeedback = false;
      }
    },
    handleCloseWindow() {
      window.close();
    },
    notify(type, message) {
      if (this.$message && typeof this.$message[type] === "function") {
        this.$message[type](message);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.alert-status {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1.25rem;
  border-radius: 9999px;
  background: rgba(251, 146, 60, 0.1);
  color: #fb923c;
  border: 1px solid rgba(251, 146, 60, 0.3);
  font-size: 1rem;
  font-weight: 500;
}

.pause-alert-panel {
  margin-top: 1rem;
}

.pause-alert-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  color: #cbd5e1;
  font-size: 1rem;
  font-weight: 500;
}

.pause-alert-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.pause-alert-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.pause-alert-label {
  color: #94a3b8;
  font-size: 0.95rem;
}

.pause-alert-hint {
  margin-top: 0.75rem;
  color: #94a3b8;
  font-size: 0.875rem;
  line-height: 1.4;
}

@media (max-width: 640px) {
  .pause-alert-grid {
    grid-template-columns: 1fr;
  }
}
</style>
